<script setup lang="ts">

import {ProductRecommendation} from "@/core/interfaces/Product";
import BaseImage from "@/core/components/base/BaseImage.vue";
import {price as formatPrice} from "@/core/helpers/formatHelper";
import {LucideCheckCircle, PlusCircle} from "@lucide/vue";
import {computed, ref} from "vue";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/core/components/ui/accordion";
import {useSystemStore} from "@/stores/system.store";

const {recommendation} = defineProps<{
    recommendation: ProductRecommendation;
}>();

const emits = defineEmits(['added', 'removed']);

const added = ref<boolean>();

const toggle = () => {
    if (!added.value) {
        emits('added');
        added.value = true;
    } else {
        emits('removed');
        added.value = false;
    }
};

const stock = computed(() => recommendation.stock ?? 0);
const price = computed(() => recommendation.price ?? 0);

const systemStore = useSystemStore();

/**
 * The logo of the grouping covering the promotion of this product, displayed
 * next to the product name. A laboratory may not promote a named product on its
 * own: the logo is what makes the advice lawful, so it is only shown when the
 * pharmacy actually belongs to the covering grouping.
 */
const coveringGroupingLogo = computed(() => {
    const covered = !!recommendation.grouping_id && recommendation.grouping_id === systemStore.groupingId;

    return covered ? systemStore.groupingLogo : undefined;
});

</script>

<template>
  <div class="flex rounded bg-white p-3 gap-4 min-h-32 justify-space-between">
    <div class="flex flex-col w-24 gap-3">
      <BaseImage
        v-if="recommendation.product.image_url"
        :path="recommendation.product.image_url"
        :text="recommendation.product.name"
        class="w-full h-24 object-fit rounded"
      />
      <div v-else class="bg-gray-100 w-full h-24 rounded" />
      <div class="flex justify-center text-sm">
        {{ price > 0 ? formatPrice(price) : ' - ' }}
      </div>
    </div>
    <div class="flex-1 flex flex-col gap-1">
      <div class="flex items-start justify-between gap-2">
        <p>{{ recommendation.title || recommendation.product.name }}</p>
        <div
          v-if="coveringGroupingLogo"
          class="shrink-0 rounded px-1.5 py-1"
          :style="{backgroundColor: systemStore.groupingLogoBackground}"
        >
          <img
            :src="coveringGroupingLogo"
            :alt="systemStore.groupingName ? `Logo ${systemStore.groupingName}` : 'Logo du groupement'"
            :title="systemStore.groupingName"
            class="h-6 object-contain"
          >
        </div>
      </div>
      <MarkdownView v-if="recommendation.description" :content="recommendation.description" class="text-sm leading-tight px-0 py-2" />
      <div class="text-gray-600 text-sm">
        <p class="text-sm">
          <span :class="stock > 0 ? '' : 'text-red-500'">{{ stock }}</span> en stock
        </p>
        <Accordion v-if="recommendation.posology || recommendation.comments" type="single" collapsible>
          <AccordionItem value="item-1" class="border-none">
            <AccordionTrigger class="text-primary pb-0 justify-start gap-2">
              En savoir plus
            </AccordionTrigger>
            <AccordionContent>
              <div class="flex flex-col gap-1 text-sm">
                <div>{{ recommendation.posology }}</div>
                <div>{{ recommendation.comments }}</div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
    <div class="flex items-center">
      <div>
        <button @click="toggle">
          <PlusCircle v-if="!added" class="text-primary" />
          <LucideCheckCircle v-else class="text-green" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
